<template>
  <DropdownHybrid
    v-model="dropdownOpen"
    ref="dropdown"
    :sheet-title="t('page.bookmarks.layout.sheet')"
    :menu-props="{start: true, minWidth: 240}"
  >
    <template #activator="{ props: menuProps }">
      <v-tooltip
        :open-on-hover="true"
        location="top" :open-delay="300"
        content-class="text-neutral0 bg-neutral900"
        :disabled="isMobile || dropdownOpen"
      >
        <span>{{ t('cta.view') }}</span>
        <template #activator="{ props: tooltipProps }">
          <BrandButton
            variant="secondary"
            color="neutral25 text-neutral900"
            :class="$attrs.class"
            v-bind="{ ...tooltipProps, ...menuProps, ...toolbarBtnProps }"
            flat
          >
            <component :is="getStyleIcon(listStyle)" class="text-h5" />
            <span v-if="!isCompact" class="ml-3">{{ t(getStyleText(listStyle)) }}</span>
          </BrandButton>
        </template>
      </v-tooltip>
    </template>

    <v-list bg-color="neutral25" class="py-3">
      <v-list-item v-for="option in options" :key="option.key" @click="onSelect(option.value)">
        <template #prepend>
          <component :is="option.icon" class="text-h5 mr-3" />
        </template>
        <v-list-item-title class="text-neutral900 font-weight-medium">
          {{ t(option.text) }}
        </v-list-item-title>
      </v-list-item>
    </v-list>
  </DropdownHybrid>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue'
import { useDisplay } from 'vuetify'
import { useI18n } from 'vue-i18n'
import { useToolbarButtonStyle } from '@/composables/useToolbarButtonStyle'
import {
  PhListBullets, PhList, PhSquaresFour, PhRows, PhGridFour,
} from '@phosphor-icons/vue'
import { ITEM_LAYOUT_STYLE } from '@/utils/constants'

const ICON_CARDS_DESKTOP = PhSquaresFour
const ICON_TILES_DESKTOP = PhGridFour
const ICONS = {
  [ITEM_LAYOUT_STYLE.cards]: PhRows,
  [ITEM_LAYOUT_STYLE.tiles]: PhRows,
  [ITEM_LAYOUT_STYLE.list]: PhListBullets,
  [ITEM_LAYOUT_STYLE.headlines]: PhList,
}

const TEXTS = {
  [ITEM_LAYOUT_STYLE.cards]: 'component.bookmarks.style.list.cards',
  [ITEM_LAYOUT_STYLE.tiles]: 'component.bookmarks.style.list.tiles',
  [ITEM_LAYOUT_STYLE.list]: 'component.bookmarks.style.list.list',
  [ITEM_LAYOUT_STYLE.headlines]: 'component.bookmarks.style.list.headlines',
}

// eslint-disable-next-line no-unused-vars
const props = defineProps({
  listStyle: {
    type: String,
    required: true,
  },
})

const emit = defineEmits(['set'])
const { t } = useI18n()
const display = useDisplay()
const isMobile = computed(() => display.smAndDown.value)
const { isCompact, toolbarBtnProps } = useToolbarButtonStyle()
const dropdown = ref(null)
const dropdownOpen = ref(false)

const options = computed(() => {
  return Object.keys(ITEM_LAYOUT_STYLE).map(value => ({
    icon: getStyleIcon(value),
    text: getStyleText(value),
    value,
    key: value, // Add a key property for v-for
  }))
})

function onSelect(val) {
  dropdown.value?.close()
  emit('set', val)
}

function getStyleIcon(style) {
  const smAndUp = display.smAndUp.value
  if (style === ITEM_LAYOUT_STYLE.cards && smAndUp) {
    return ICON_CARDS_DESKTOP
  }

  if (style === ITEM_LAYOUT_STYLE.tiles && smAndUp) {
    return ICON_TILES_DESKTOP
  }

  return ICONS[style]
}

function getStyleText(style) {
  return TEXTS[style]
}
</script>
